import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Button, Select, Row, Input } from 'antd';
import { useHistory } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import GetField from '../Components/GetField';
import styles from '../styles';
import { successNotification, errorNotification } from '../Snackbar';
import Api from '../Api';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import {
  getCustomers,
  clearCustomers,
  clearProductIds,
  getProductIds,
  getFactoryIds,
  clearFactoryIds,
  getRackIds,
  clearRackIds,
  getAccessoriesForJobchart,
  clearAccessoriesForJobchart,
  getActiveJobchartIds,
  clearActiveJobchartIds,
  getSingleAccessoryInfoToTransfer,
  clearSingleAccessoryInfoToTransfer,
} from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';

const AntInput = GetField(Input);
const AntSelect = GetField(Select);
const { Option } = Select;
const AntInputTextArea = GetField(Input.TextArea);

const TransferForm = props => {
  const { classes, match } = props;
  const history = useHistory();
  const dispatch = useDispatch();

  const { handleSubmit, control, reset, getValues, watch, setValue } =
    useForm();

  const [confirmLoading, setConfirmLoading] = useState(false);

  const customers = useSelector(state => state.customers);
  const productIds = useSelector(state => state.productIds);
  const activeJobchartIds = useSelector(state => state.activeJobchartIds);
  const factoryIds = useSelector(state => state.factoryIds);
  const rackIds = useSelector(state => state.rackIds);
  const accessoriesForJobchart = useSelector(
    state => state.accessoriesForJobchart
  );
  const accessoryInfoToTransfer = useSelector(
    state => state.accessoryInfoToTransfer
  );

  useEffect(() => {
    dispatch(getCustomers());
    dispatch(getFactoryIds());
    dispatch(getRackIds());
    dispatch(getAccessoriesForJobchart());
    dispatch(getSingleAccessoryInfoToTransfer(match.params.id));
    return () => {
      dispatch(clearCustomers());
      dispatch(clearFactoryIds());
      dispatch(clearRackIds());
      dispatch(clearAccessoriesForJobchart());
      dispatch(clearSingleAccessoryInfoToTransfer());
    };
  }, []);

  useEffect(() => {
    reset(accessoryInfoToTransfer);
  }, [accessoryInfoToTransfer]);

  useEffect(() => {
    const customerId = getValues('customerId');
    if (!customerId) {
      dispatch(clearProductIds());
      setValue('targetProductId', null);
    } else {
      dispatch(getProductIds(customerId));
    }
  }, [watch('customerId')]);

  useEffect(() => {
    const productId = getValues('targetProductId');
    if (!productId) {
      dispatch(clearActiveJobchartIds());
      setValue('jobchartId', null);
    } else {
      dispatch(getActiveJobchartIds({ productId }));
    }
  }, [watch('targetProductId')]);

  const onSubmit = useCallback(async formValues => {
    try {
      setConfirmLoading(true);
      await Api.post(`/accessory-inventory/transfer/request`, {
        ...formValues,
        accessoryInventoryId: match.params.id,
      });
      successNotification('Accessory transfer requested');
      history.push('/inventory/accessory');
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, []);

  return (
    <>
      <Breadcrumbs
        moduleName="Accessory Inventory"
        moduleLink="/inventory/accessory"
        subModuleName="Transfer Request"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <LeftGrid>
            <Controller
              name="customer"
              control={control}
              render={({ field }) => (
                <AntInput
                  disabled
                  label="Customer"
                  placeholder="Customer"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Customer"
                  placeholder="Select customer"
                  {...field}
                >
                  {customers.map(customer => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="product"
              control={control}
              render={({ field }) => (
                <AntInput
                  disabled
                  label="Product"
                  placeholder="Product"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="targetProductId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Product"
                  placeholder="Select product"
                  {...field}
                >
                  {productIds.map(product => (
                    <Option key={product.id} value={product.id}>
                      {product.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="bpoNo"
              control={control}
              render={({ field }) => (
                <AntInput
                  disabled
                  label="BPO #"
                  placeholder="BPO #"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="targetJobchartId"
              control={control}
              render={({ field }) => (
                <AntSelect label="BPO #" placeholder="Select bpo" {...field}>
                  {activeJobchartIds.map(jobchart => (
                    <Option key={jobchart.id} value={jobchart.id}>
                      {jobchart.bpoNo}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="accessory"
              control={control}
              render={({ field }) => (
                <AntInput
                  disabled
                  label="Accessory"
                  placeholder="Accessory"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="targetAccessoryId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Accessory"
                  placeholder="Select accessory"
                  {...field}
                >
                  {accessoriesForJobchart.map(accessory => (
                    <Option key={accessory.id} value={accessory.id}>
                      {accessory.accessory}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="factory"
              control={control}
              render={({ field }) => (
                <AntInput
                  disabled
                  label="Factory"
                  placeholder="Factory"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="targetFactoryId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Factory"
                  placeholder="Select factory"
                  {...field}
                >
                  {factoryIds.map(factory => (
                    <Option key={factory.id} value={factory.id}>
                      {factory.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="rackno"
              control={control}
              render={({ field }) => (
                <AntInput
                  disabled
                  label="Rack #"
                  placeholder="Rack #"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="targetRacknoId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Rack #"
                  placeholder="Select rack"
                  {...field}
                >
                  {rackIds.map(rack => (
                    <Option key={rack.id} value={rack.id}>
                      {rack.rackno}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="quantity"
              control={control}
              render={({ field }) => (
                <AntInput
                  disabled
                  label="Quantity"
                  placeholder="Quantity"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="requestedQuantity"
              control={control}
              render={({ field }) => (
                <AntInput
                  allowClear
                  type="number"
                  min={0}
                  step="any"
                  label="Quantity"
                  placeholder="Quantity"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid />
          <RightGrid>
            <Controller
              name="requestedRemark"
              control={control}
              render={({ field }) => (
                <AntInputTextArea
                  allowClear
                  label="Remark"
                  placeholder="Remark"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row className={classes.buttonContainer}>
          <Button type="primary" loading={confirmLoading} htmlType="submit">
            Submit
          </Button>
        </Row>
      </form>
    </>
  );
};

TransferForm.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(TransferForm);
